import {
  Card,
  CardContent,
  CardHeader,
  FormControlLabel,
  Grid2,
  IconButton,
  Stack,
  Switch,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";

import DashboardLayout from "../../../layoutcontainers/DashboardLayout";
import DashboardFooter from "../../../layoutcontainers/DashboardLayout/components/Footer";
import MaterialTypography from "../../../components/MaterialTypography";
import MaterialInput from "../../../components/MaterialInput";
import MaterialSelect from "../../../components/MasterSelect";
import MaterialButton from "../../../components/MaterialButton";
import MaterialBox from "../../../components/MaterialBox";
import { useSnackbar } from "../../../context/popup";

import { fetchData, fetchDataById, postData, putData } from "../../../services";
import { useAPIEndpointOperationFormState } from "./components/APIoperationsStateHandle";
import DropDownConfig from "../../Utils/DropDownConfig";

const APIoperationsForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showSnackbar } = useSnackbar();

  const recordId = searchParams.get("id");
  const pageType = searchParams.get("pageType");

  const pageMode =
    pageType === "view" ? "view" : pageType === "edit" ? "edit" : "add";

  const { formData, setFormData, handleChange, checkAllFields } =
    useAPIEndpointOperationFormState();


  /* ================= DROPDOWNS ================= */
  const [apiRegistryOptions, setApiRegistryOptions] = useState([]);
  const [apiCategoryOptions, setApiCategoryOptions] = useState([]);
  const [protocolOptions, setProtocolOptions] = useState([]);
  const [httpMethodOptions, setHttpMethodOptions] = useState([]);
  const [graphqlOperationOptions, setGraphqlOperationOptions] = useState([]);
  const [apiEndpointOptions, setApiEndpointOptions] = useState([]);
  const [requestSchemaOptions, setRequestSchemaOptions] = useState([]);
  const [responseSchemaOptions, setResponseSchemaOptions] = useState([]);
  const [notificationSchemaOptions, setNotificationSchemaOptions] = useState(
    []
  );

  useEffect(() => {
    const loadDropdowns = async () => {
      const results = await Promise.allSettled([
        fetchData(`/api-engine/api-registry?dropdown=true&isActive=true`),
        fetchData(`/api-engine/key-value-store?dropdown=true&code=${DropDownConfig?.apiApiCategory}`),
        fetchData(`/api-engine/api-endpoint?dropdown=true&isActive=true`),
        fetchData(`/api-engine/key-value-store?dropdown=true&code=${DropDownConfig?.apiProtocol}`),
        fetchData(`/api-engine/key-value-store?dropdown=true&code=${DropDownConfig?.apiHttpMethod}`),
        fetchData(`/api-engine/key-value-store?dropdown=true&code=${DropDownConfig?.apiGraphQLOperationType}`),
        fetchData(`/api-engine/schema?dropdown=true&isActive=true`),
        fetchData(`/api-engine/schema?dropdown=true&isActive=true`),
        fetchData(`/api-engine/schema?dropdown=true&isActive=true`),
      ]);

      const [
        apiRegistryRes,
        apiCategoryRes,
        apiEndpointRes,
        protocolRes,
        httpMethodRes,
        graphqlOpRes,
        reqSchemaRes,
        resSchemaRes,
        notSchemaRes,
      ] = results;

      if (apiRegistryRes.status === "fulfilled")
        setApiRegistryOptions(
          apiRegistryRes.value?.data?.items?.map((i) => ({
            label: i.name,
            value: i.id,
          })) || []
        );

      if (apiCategoryRes.status === "fulfilled")
        setApiCategoryOptions(
          apiCategoryRes.value?.data?.items?.map((i) => ({
            label: i.keyName,
            value: i.id,
          })) || []
        );

      if (apiEndpointRes.status === "fulfilled")
        setApiEndpointOptions(
          apiEndpointRes.value?.data?.items?.map((i) => ({
            label: i.endpointUrl,
            value: i.id,
          })) || []
        );

      if (protocolRes.status === "fulfilled")
        setProtocolOptions(
          protocolRes.value?.data?.items?.map((i) => ({
            label: i.keyName,
            value: i.id,
          })) || []
        );

      if (httpMethodRes.status === "fulfilled")
        setHttpMethodOptions(
          httpMethodRes.value?.data?.items?.map((i) => ({
            label: i.keyName,
            value: i.id,
          })) || []
        );

      if (graphqlOpRes.status === "fulfilled")
        setGraphqlOperationOptions(
          graphqlOpRes.value?.data?.items?.map((i) => ({
            label: i.keyName,
            value: i.id,
          })) || []
        );

      if (reqSchemaRes.status === "fulfilled")
        setRequestSchemaOptions(
          reqSchemaRes.value?.data?.items?.map((i) => ({
            label: i.name,
            value: i.id,
          })) || []
        );
      if (resSchemaRes.status === "fulfilled")
        setResponseSchemaOptions(
          resSchemaRes.value?.data?.items?.map((i) => ({
            label: i.name,
            value: i.id,
          })) || []
        );
      if (notSchemaRes.status === "fulfilled")
        setNotificationSchemaOptions(
          notSchemaRes.value?.data?.items?.map((i) => ({
            label: i.name,
            value: i.id,
          })) || []
        );
      // Optional warning if any API failed
      if (results.some((r) => r.status === "rejected")) {
        showSnackbar("Some dropdowns failed to load", "warning");
      }
    };

    loadDropdowns();
  }, []);

  /* ================= EDIT ================= */
  useEffect(() => {
    if (!recordId) return;

    const loadDetails = async () => {
      const res = await fetchDataById(`/api-engine/api-operation/${recordId}`);
      if (!res?.success) return;

      const d = res.data;

      setFormData((prev) => ({
        ...prev,

        // ===== Select fields =====
        apiRegistryId: {
          label: d.apiRegistry.name,
          value: d.apiRegistry.id,
        },
        apiCategoryId: {
          label: d.apiCategory.keyName,
          value: d.apiCategory.id,
        },
        protocolId: {
          label: d.protocol.keyName,
          value: d.protocol.id,
        },
        httpMethodId: {
          label: d.httpMethod.keyName,
          value: d.httpMethod.id,
        },
        graphqlOperationTypeId: d.graphqlOperationType
          ? {
              label: d.graphqlOperationType.keyName,
              value: d.graphqlOperationType.id,
            }
          : null,

        // ===== Text fields =====
        operationCode: d.operationCode,
        soapAction: d.soapAction ?? "",
        pathTemplate: d.pathTemplate,
        version: d.version,

        // ===== Schema fields =====
        requestSchemaId: d.requestSchema
          ? {
              label: d.requestSchema.name,
              value: d.requestSchema.id,
            }
          : null,
        responseSchemaId: d.responseSchema
          ? {
              label: d.responseSchema.name,
              value: d.responseSchema.id,
            }
          : null,
        notificationSchemaId: d.notificationSchema
          ? {
              label: d.notificationSchema.name,
              value: d.notificationSchema.id,
            }
          : null,

        // ===== JSON fields =====
        headersConfig: JSON.stringify(d.headersConfig ?? {}, null, 2),
        payloadExample: JSON.stringify(d.payloadExample ?? {}, null, 2),

        // ===== Status =====
        isActive: d.isActive,

        // ===== SUCCESS STATES (ALL) =====
        apiRegistryIdStatus: "success",
        apiCategoryIdStatus: "success",
        protocolIdStatus: "success",
        httpMethodIdStatus: "success",
        graphqlOperationTypeIdStatus: "success",

        operationCodeStatus: "success",
        soapActionStatus: "success",
        pathTemplateStatus: "success",
        versionStatus: "success",

        requestSchemaIdStatus: "success",
        responseSchemaIdStatus: "success",
        notificationSchemaIdStatus: "success",

        headersConfigStatus: "success",
        payloadExampleStatus: "success",

        isActiveStatus: "success",
      }));
    };

    loadDetails();
  }, [recordId]);

  /* ================= SUBMIT ================= */
  const handleSubmit = async () => {
    const payload = {
      apiRegistryId: formData.apiRegistryId?.value,
      apiCategoryId: formData.apiCategoryId?.value,
      // apiEndpointId: formData.apiEndpointId?.value,
      apiEndpointId: "b9b01533-0bd5-4ef8-aed3-c4f3ca45b35c",

      protocolId: formData.protocolId?.value,
      httpMethodId: formData.httpMethodId?.value,
      graphqlOperationTypeId: formData.graphqlOperationTypeId?.value,

      operationCode: formData.operationCode,
      soapAction: formData.soapAction,
      pathTemplate: formData.pathTemplate,

      requestSchemaId: formData.requestSchemaId?.value,
      responseSchemaId: formData.responseSchemaId?.value,
      notificationSchemaId: formData.notificationSchemaId?.value,

      headersConfig: formData.headersConfig
        ? JSON.parse(formData.headersConfig)
        : {},

      payloadExample: formData.payloadExample
        ? JSON.parse(formData.payloadExample)
        : {},

      version: formData.version,
      isActive: formData.isActive,
    };

    const res = recordId
      ? await putData(`/api-engine/api-operation/${recordId}`, payload)
      : await postData("/api-engine/api-operation", payload);

    if (res?.success) {
      showSnackbar(res.message, "success");
      navigate(-1);
    } else {
      showSnackbar(res?.message || "Failed", "error");
    }
  };

  /* ================= UI ================= */
  return (
    <>
      <DashboardLayout>
        <Card sx={{ p: 2 }}>
          <CardHeader
            title={
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton onClick={() => navigate(-1)}>
                  <ArrowBack />
                </IconButton>
                <MaterialTypography variant="h5">
                  API Operation
                </MaterialTypography>
              </Stack>
            }
          />

          <CardContent>
            <Grid2 container spacing={3}>
              {/* ================= BASIC CONFIG ================= */}
              <Grid2 size={{ xs: 12 }}>
                <MaterialBox p={2} shadow="sm">
                  <MaterialTypography variant="subtitle2" fontWeight="bold">
                    Basic Configuration
                  </MaterialTypography>

                  <Grid2 container spacing={2}>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <MaterialTypography variant="caption">
                        API Registry
                      </MaterialTypography>
                      <MaterialSelect
                        value={formData.apiRegistryId}
                        options={apiRegistryOptions}
                        onChange={(opt) =>
                          handleChange(opt, { name: "apiRegistryId" })
                        }
                        success={formData.apiRegistryIdStatus === "success"}
                        error={formData.apiRegistryIdStatus === "error"}
                        isDisabled={pageMode === "view"}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <MaterialTypography variant="caption">
                        API Category
                      </MaterialTypography>
                      <MaterialSelect
                        value={formData.apiCategoryId}
                        options={apiCategoryOptions}
                        onChange={(opt) =>
                          handleChange(opt, { name: "apiCategoryId" })
                        }
                        success={formData.apiCategoryIdStatus === "success"}
                        error={formData.apiCategoryIdStatus === "error"}
                        isDisabled={pageMode === "view"}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <MaterialTypography variant="caption">
                        API Endpoint
                      </MaterialTypography>
                      <MaterialSelect
                        value={formData.apiEndpointId}
                        options={apiEndpointOptions}
                        onChange={(opt) =>
                          handleChange(opt, { name: "apiEndpointId" })
                        }
                        success={formData.apiEndpointIdStatus === "success"}
                        error={formData.apiEndpointIdStatus === "error"}
                        isDisabled={pageMode === "view"}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <MaterialTypography variant="caption">
                        Operation Code
                      </MaterialTypography>
                      <MaterialInput
                        name="operationCode"
                        value={formData.operationCode}
                        onChange={handleChange}
                        success={formData.operationCodeStatus === "success"}
                        error={formData.operationCodeStatus === "error"}
                        disabled={pageMode === "view"}
                      />
                    </Grid2>
                  </Grid2>
                </MaterialBox>
              </Grid2>

              {/* ================= PROTOCOL CONFIG ================= */}
              <Grid2 size={{ xs: 12 }}>
                <MaterialBox p={2} shadow="sm">
                  <MaterialTypography variant="subtitle2" fontWeight="bold">
                    Protocol Configuration
                  </MaterialTypography>

                  <Grid2 container spacing={2}>
                    <Grid2 size={{ xs: 12, md: 4 }}>
                      <MaterialTypography variant="caption">
                        Protocol
                      </MaterialTypography>
                      <MaterialSelect
                        value={formData.protocolId}
                        options={protocolOptions}
                        onChange={(opt) =>
                          handleChange(opt, { name: "protocolId" })
                        }
                        success={formData.protocolIdStatus === "success"}
                        error={formData.protocolIdStatus === "error"}
                        isDisabled={pageMode === "view"}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 4 }}>
                      <MaterialTypography variant="caption">
                        HTTP Method
                      </MaterialTypography>
                      <MaterialSelect
                        value={formData.httpMethodId}
                        options={httpMethodOptions}
                        onChange={(opt) =>
                          handleChange(opt, { name: "httpMethodId" })
                        }
                        success={formData.httpMethodIdStatus === "success"}
                        error={formData.httpMethodIdStatus === "error"}
                        isDisabled={pageMode === "view"}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 4 }}>
                      <MaterialTypography variant="caption">
                        GraphQL Operation Type
                      </MaterialTypography>
                      <MaterialSelect
                        value={formData.graphqlOperationTypeId}
                        options={graphqlOperationOptions}
                        onChange={(opt) =>
                          handleChange(opt, { name: "graphqlOperationTypeId" })
                        }
                        success={
                          formData.graphqlOperationTypeIdStatus === "success"
                        }
                        error={
                          formData.graphqlOperationTypeIdStatus === "error"
                        }
                        isDisabled={pageMode === "view"}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <MaterialTypography variant="caption">
                        SOAP Action
                      </MaterialTypography>
                      <MaterialInput
                        name="soapAction"
                        value={formData.soapAction}
                        onChange={handleChange}
                        disabled={pageMode === "view"}
                        success={formData.soapActionStatus === "success"}
                        error={formData.soapActionStatus === "error"}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <MaterialTypography variant="caption">
                        Path Template
                      </MaterialTypography>
                      <MaterialInput
                        name="pathTemplate"
                        value={formData.pathTemplate}
                        onChange={handleChange}
                        success={formData.pathTemplateStatus === "success"}
                        error={formData.pathTemplateStatus === "error"}
                        disabled={pageMode === "view"}
                      />
                    </Grid2>
                  </Grid2>
                </MaterialBox>
              </Grid2>

              {/* ================= SCHEMA CONFIG ================= */}
              <Grid2 size={{ xs: 12 }}>
                <MaterialBox p={2} shadow="sm">
                  <MaterialTypography variant="subtitle2" fontWeight="bold">
                    Schema Configuration
                  </MaterialTypography>

                  <Grid2 container spacing={2}>
                    {/* REQUEST SCHEMA */}
                    <Grid2 size={{ xs: 12, md: 4 }}>
                      <MaterialTypography variant="caption">
                        Request Schema
                      </MaterialTypography>

                      <MaterialSelect
                        value={formData.requestSchemaId}
                        options={requestSchemaOptions}
                        onChange={(opt) =>
                          handleChange(opt, { name: "requestSchemaId" })
                        }
                        success={formData.requestSchemaIdStatus === "success"}
                        error={formData.requestSchemaIdStatus === "error"}
                        isDisabled={pageMode === "view"}
                      />
                    </Grid2>

                    {/* RESPONSE SCHEMA */}
                    <Grid2 size={{ xs: 12, md: 4 }}>
                      <MaterialTypography variant="caption">
                        Response Schema
                      </MaterialTypography>

                      <MaterialSelect
                        value={formData.responseSchemaId}
                        options={responseSchemaOptions}
                        onChange={(opt) =>
                          handleChange(opt, { name: "responseSchemaId" })
                        }
                        success={formData.responseSchemaIdStatus === "success"}
                        error={formData.responseSchemaIdStatus === "error"}
                        isDisabled={pageMode === "view"}
                      />
                    </Grid2>

                    {/* NOTIFICATION SCHEMA */}
                    <Grid2 size={{ xs: 12, md: 4 }}>
                      <MaterialTypography variant="caption">
                        Notification Schema
                      </MaterialTypography>

                      <MaterialSelect
                        value={formData.notificationSchemaId}
                        options={notificationSchemaOptions}
                        onChange={(opt) =>
                          handleChange(opt, { name: "notificationSchemaId" })
                        }
                        success={
                          formData.notificationSchemaIdStatus === "success"
                        }
                        error={formData.notificationSchemaIdStatus === "error"}
                        isDisabled={pageMode === "view"}
                      />
                    </Grid2>
                  </Grid2>
                </MaterialBox>
              </Grid2>

              {/* ================= HEADERS & PAYLOAD ================= */}
              <Grid2 size={{ xs: 12 }}>
                <MaterialBox p={2} shadow="sm">
                  <MaterialTypography variant="subtitle2" fontWeight="bold">
                    Headers & Payload
                  </MaterialTypography>

                  <Grid2 container spacing={2}>
                    <Grid2 size={{ xs: 12 }}>
                      <MaterialTypography variant="caption">
                        Headers Configuration (JSON)
                      </MaterialTypography>
                      <MaterialInput
                        name="headersConfig"
                        multiline
                        rows={4}
                        value={formData.headersConfig}
                        onChange={handleChange}
                        success={formData.headersConfigStatus === "success"}
                        error={formData.headersConfigStatus === "error"}
                        disabled={pageMode === "view"}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12 }}>
                      <MaterialTypography variant="caption">
                        Payload Example (JSON)
                      </MaterialTypography>
                      <MaterialInput
                        name="payloadExample"
                        multiline
                        rows={4}
                        value={formData.payloadExample}
                        onChange={handleChange}
                        success={formData.payloadExampleStatus === "success"}
                        error={formData.payloadExampleStatus === "error"}
                        disabled={pageMode === "view"}
                      />
                    </Grid2>
                  </Grid2>
                </MaterialBox>
              </Grid2>

              {/* ================= VERSION & STATUS ================= */}
              <Grid2 size={{ xs: 12 }}>
                <MaterialBox p={2} shadow="sm">
                  <MaterialTypography variant="subtitle2" fontWeight="bold">
                    Version & Status
                  </MaterialTypography>

                  <Grid2 container spacing={2}>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <MaterialTypography variant="caption">
                        Version
                      </MaterialTypography>
                      <MaterialInput
                        name="version"
                        value={formData.version}
                        onChange={handleChange}
                        success={formData.versionStatus === "success"}
                        error={formData.versionStatus === "error"}
                        disabled={pageMode === "view"}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 6 }} px={3}>
                      <MaterialTypography variant="caption" ml={-2}>
                        Status
                      </MaterialTypography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.isActive}
                            onChange={(e) =>
                              handleChange({
                                target: {
                                  name: "isActive",
                                  value: e.target.checked,
                                },
                              })
                            }
                            disabled={pageMode === "view"}
                          />
                        }
                        label={formData.isActive ? "Active" : "Inactive"}
                      />
                    </Grid2>
                  </Grid2>
                </MaterialBox>
              </Grid2>
            </Grid2>
          </CardContent>

          {pageMode !== "view" && (
            <MaterialBox display="flex" justifyContent="flex-end">
              <MaterialButton
                variant="gradient"
                color="dark"
                disabled={!checkAllFields()}
                onClick={handleSubmit}
              >
                {pageMode === "edit" ? "Update" : "Submit"}
              </MaterialButton>
            </MaterialBox>
          )}
        </Card>
      </DashboardLayout>
      <DashboardFooter />
    </>
  );
};

export default APIoperationsForm;
